import { useCallback, useMemo, useState } from "react";
import {
  Filter,
  MailPlus,
  MailX,
  RotateCw,
  Send,
  ShieldCheck,
  Undo2,
} from "lucide-react";
import {
  ROLE_LABELS,
  inviteStatus,
  type Invite,
  type InviteStatus,
} from "@ir/types";
import {
  formatDateTime,
  formatRelativeTime,
  listInvites,
  resendInvite,
  revokeInvite,
} from "@ir/services";
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  Menu,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  Toolbar,
  useToast,
  type BadgeTone,
  type Column,
  type SortState,
} from "@ir/ui";
import { ColumnVisibilityButton } from "../components/ColumnVisibilityButton";
import { useAsyncData } from "../hooks/useAsyncData";
import { useColumnVisibility } from "../hooks/useColumnVisibility";
import { InviteDialog } from "../features/access/InviteDialog";

const PAGE_SIZE = 25;
type InviteSortBy = "email" | "role" | "expires_at" | "created_at";

function inviteSortByForColumn(columnId: string): InviteSortBy {
  if (columnId === "expires") return "expires_at";
  if (columnId === "created") return "created_at";
  if (["email", "role"].includes(columnId)) return columnId as InviteSortBy;
  return "created_at";
}

const STATUS_OPTIONS: { value: InviteStatus | ""; label: string }[] = [
  { value: "", label: "All invites" },
  { value: "pending", label: "Pending" },
  { value: "accepted", label: "Accepted" },
  { value: "expired", label: "Expired" },
  { value: "revoked", label: "Revoked" },
];

const STATUS_TONE: Record<InviteStatus, BadgeTone> = {
  pending: "warning",
  accepted: "success",
  expired: "neutral",
  revoked: "danger",
};

export function InvitesPage() {
  const toast = useToast();

  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState<InviteStatus | "">("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ columnId: "created", direction: "desc" });
  const [refreshToken, setRefreshToken] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<Invite | null>(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);

  const result = useAsyncData(
    (signal) =>
      listInvites({
        searchTerm,
        status,
        pageNumber: page,
        pageSize: PAGE_SIZE,
        sortBy: inviteSortByForColumn(sort.columnId),
        sortDirection: sort.direction,
        signal,
      }),
    [searchTerm, status, page, sort, refreshToken],
  );

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setSaving(true);
    try {
      await revokeInvite(revokeTarget.id);
      toast.success("Invite revoked", `The link sent to ${revokeTarget.email} no longer works.`);
      setRevokeTarget(null);
      refresh();
    } catch (error) {
      toast.error("Could not revoke that invite", error instanceof Error ? error.message : undefined);
    } finally {
      setSaving(false);
    }
  };

  const resend = async (invite: Invite) => {
    try {
      await resendInvite(invite.id);
      toast.success("Invite resent", `A fresh link is on its way to ${invite.email}.`);
      refresh();
    } catch (error) {
      toast.error("Could not resend that invite", error instanceof Error ? error.message : undefined);
    }
  };

  const columns = useMemo<Column<Invite>[]>(
    () => [
      {
        id: "email",
        header: "Invited",
        sortable: true,
        cell: (invite) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-content">{invite.email}</p>
            <p className="truncate text-xs text-content-subtle">
              by {invite.invited_by}
            </p>
          </div>
        ),
      },
      {
        id: "role",
        header: "Role",
        width: "10rem",
        sortable: true,
        cell: (invite) => (
          <Badge tone="gold" icon={ShieldCheck}>
            {ROLE_LABELS[invite.role]}
          </Badge>
        ),
      },
      {
        id: "status",
        header: "Status",
        width: "8rem",
        cell: (invite) => {
          const state = inviteStatus(invite);
          return (
            <Badge tone={STATUS_TONE[state]} dot>
              {state.charAt(0).toUpperCase() + state.slice(1)}
            </Badge>
          );
        },
      },
      {
        id: "expires",
        header: "Expires",
        hideBelow: "md",
        sortable: true,
        cell: (invite) => (
          <span
            className="whitespace-nowrap text-content-muted"
            title={formatDateTime(invite.expires_at)}
          >
            {formatRelativeTime(invite.expires_at)}
          </span>
        ),
      },
      {
        id: "created",
        header: "Sent",
        hideBelow: "xl",
        sortable: true,
        cell: (invite) => (
          <span className="whitespace-nowrap text-content-muted">
            {formatDateTime(invite.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        mobileLabel: "Actions",
        align: "right",
        width: "4rem",
        cell: (invite) => {
          const state = inviteStatus(invite);
          const isOpen = state === "pending";
          return (
            <Menu
              label={`Actions for the invite to ${invite.email}`}
              items={[
                {
                  id: "resend",
                  label: state === "accepted" ? "Send a new invite" : "Resend invite",
                  icon: Send,
                  disabled: state === "revoked",
                  onSelect: () => void resend(invite),
                },
                {
                  id: "revoke",
                  label: "Revoke invite",
                  icon: Undo2,
                  danger: true,
                  separated: true,
                  disabled: !isOpen,
                  onSelect: () => setRevokeTarget(invite),
                },
              ]}
              trigger={({ open, toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  aria-expanded={open}
                  aria-label={`Actions for ${invite.email}`}
                  className="grid size-8 place-items-center rounded-md text-content-subtle transition-colors hover:bg-surface-sunken hover:text-content"
                >
                  ⋯
                </button>
              )}
            />
          );
        },
      },
    ],
    // `resend` closes over `refresh`, which is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const columnVisibility = useColumnVisibility(
    "invites",
    columns,
    ["email", "role", "status", "expires", "created"],
  );

  const updateSort = (next: SortState) => {
    setSort(next);
    setPage(1);
  };

  return (
    <>
      <PageHeader
        icon={MailPlus}
        title="Invites"
        description="Outstanding and historic invitations. Links expire after 48 hours and work only once."
        actions={
          <>
            <Button
              variant="secondary"
              icon={RotateCw}
              loading={result.refreshing}
              onClick={refresh}
            >
              Refresh
            </Button>
            <Button icon={MailPlus} onClick={() => setCreateOpen(true)}>
              Invite people
            </Button>
          </>
        }
      >
        <Toolbar>
          <SearchInput
            className="flex-1"
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setPage(1);
            }}
            placeholder="Search by email address"
            ariaLabel="Search invites"
          />
          <Select
            className="sm:w-44"
            icon={Filter}
            options={STATUS_OPTIONS}
            value={status}
            aria-label="Filter by invite status"
            onChange={(event) => {
              setStatus(event.target.value as InviteStatus | "");
              setPage(1);
            }}
          />
          <ColumnVisibilityButton
            columns={columnVisibility.selectableColumns}
            visibleColumnIds={columnVisibility.visibleColumnIds}
            minimumReached={columnVisibility.minimumReached}
            onToggle={columnVisibility.toggleColumn}
            onReset={columnVisibility.resetColumns}
          />
        </Toolbar>
      </PageHeader>

      <DataTable
        caption="Invites"
        columns={columnVisibility.visibleColumns}
        rows={result.data?.items ?? []}
        rowKey={(invite) => invite.id}
        loading={result.loading}
        refreshing={result.refreshing}
        error={result.error}
        onRetry={result.reload}
        sort={sort}
        onSortChange={updateSort}
        emptyIcon={MailX}
        emptyTitle="No invites to show"
        emptyDescription="Invite a colleague and their invitation will be tracked here."
        emptyAction={
          <Button icon={MailPlus} onClick={() => setCreateOpen(true)}>
            Invite people
          </Button>
        }
      />

      {result.data && (
        <Pagination
          currentPage={result.data.pageNumber}
          totalPages={result.data.totalPages}
          totalCount={result.data.totalCount}
          pageSize={result.data.pageSize}
          disabled={result.refreshing}
          onPageChange={setPage}
        />
      )}

      <InviteDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCompleted={refresh}
      />

      <ConfirmDialog
        open={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        onConfirm={confirmRevoke}
        loading={saving}
        destructive
        title="Revoke this invite?"
        confirmLabel="Revoke invite"
        description={
          <>
            The link sent to{" "}
            <span className="font-medium text-content">{revokeTarget?.email}</span> will stop
            working immediately. You can always send a new one.
          </>
        }
      />
    </>
  );
}
